import { useRef, useState } from 'react'
import includes from 'lodash/includes'
import type { DragEvent } from 'react'

export enum DragSourceKindEnum {
  Blog = 'Blog',
  BlogCategory = 'BlogCategory',
}

/** What is being dragged, and the blog categories it cannot be dropped on — the same ones its Move to… menu hides. */
export type DragSource = { kind: DragSourceKindEnum; _id: string; excludedBlogCategoryIds: string[] }

export type UseBlogCategoryDragDropProps = {
  canEdit: boolean
  onMoveBlog: (blogId: string, categoryId: string) => void
  onMoveBlogCategory: (categoryId: string, parentId: string) => void
}

export type UseBlogCategoryDragDropResult = {
  getDragSourceProps: (source: DragSource) => Record<string, unknown>
  getDropTargetProps: (categoryId: string) => Record<string, unknown>
}

// A cross-list native handler: the source (a blog row or a blog category row) and the target (a blog category row) live in
// different lists, which `useDragSort` cannot serve. The source is held in a ref, never state — a state update in
// `dragstart` re-renders the dragged element and Chromium aborts the physical drag.
export const useBlogCategoryDragDrop = ({
  canEdit,
  onMoveBlog,
  onMoveBlogCategory,
}: UseBlogCategoryDragDropProps): UseBlogCategoryDragDropResult => {
  const sourceRef = useRef<DragSource>(undefined)
  const [dropTargetId, setDropTargetId] = useState<string>()

  const accepts = (categoryId: string) =>
    !!sourceRef.current && !includes(sourceRef.current.excludedBlogCategoryIds, categoryId)

  const getDragSourceProps = (source: DragSource) =>
    canEdit
      ? {
          draggable: true,
          onDragStart: (e: DragEvent<HTMLElement>) => {
            sourceRef.current = source
            // Firefox starts no drag without data; the ghost is the whole row, not the grip alone.
            e.dataTransfer.setData('text/plain', source._id)
            e.dataTransfer.effectAllowed = 'move'
            if (e.currentTarget.parentElement) e.dataTransfer.setDragImage(e.currentTarget.parentElement, 0, 0)
          },
          onDragEnd: () => {
            sourceRef.current = undefined
            setDropTargetId(undefined)
          },
        }
      : {}

  const onDragOver = (categoryId: string) => (e: DragEvent<HTMLElement>) => {
    if (!accepts(categoryId)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTargetId(categoryId)
  }

  const getDropTargetProps = (categoryId: string) => ({
    'data-drop-target': dropTargetId === categoryId,
    onDragEnter: onDragOver(categoryId),
    onDragOver: onDragOver(categoryId),
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTargetId(undefined)
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      if (!accepts(categoryId)) return
      e.preventDefault()
      const source = sourceRef.current
      sourceRef.current = undefined
      setDropTargetId(undefined)
      if (source.kind === DragSourceKindEnum.Blog) onMoveBlog(source._id, categoryId)
      else onMoveBlogCategory(source._id, categoryId)
    },
  })

  return { getDragSourceProps, getDropTargetProps }
}
